import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_STATUS_RENOVACAO,
  ROTULO_STATUS_REVISAO,
  STATUS_RENOVACAO,
  STATUS_REVISAO,
  formatarReais,
  formatarValorMensal,
  paraCentavos,
  type StatusRenovacao,
  type StatusRevisao,
} from '@reducee/contracts';
import { useState } from 'react';

import { chamarApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CampoCaixa, CampoSelecao, CampoTexto, CampoTextoLongo } from '../componentes/Campo.tsx';
import { Dialogo } from '../componentes/Dialogo.tsx';
import { RecusaDaApi } from '../componentes/RecusaDaApi.tsx';
import { CabecalhoDePagina, Botao, Cartao, Celula, Indicador, Linha, LinhaDeLista, Pilula, Tabela, data, type Tom, } from '../componentes/Superficie.tsx';
import { dia } from '../formulario.ts';

type Renovacao = {
  id: string;
  contato: { id: string; nome: string };
  produto: string;
  operadora: string | null;
  status: StatusRenovacao;
  statusRevisao: StatusRevisao;
  revisaoAtiva: boolean;
  dataRevisao: string | null;
  dataRenovacao: string | null;
  valorAtualCentavos: number;
  valorPropostoCentavos: number | null;
  observacoes: string | null;
  responsavel: { id: string; nome: string };
};

/** A régua de cor do protótipo: renovada é verde, perdida é vermelha, proposta
  * enviada é azul, e o que ainda está em conversa fica âmbar. */
const TOM_DO_STATUS_DA_RENOVACAO: Record<StatusRenovacao, Tom> = {
  PLANEJADA: 'alerta',
  CONTATO_INICIADO: 'alerta',
  EM_NEGOCIACAO: 'alerta',
  PROPOSTA_ENVIADA: 'informacao',
  RENOVADA: 'sucesso',
  PERDIDA: 'erro',
};

const FILTROS = [
  { valor: 'ABERTAS', rotulo: 'Abertas' },
  { valor: 'REVISAO', rotulo: 'Revisão vencida' },
  { valor: 'PROXIMAS', rotulo: 'Próximos 90 dias' },
  { valor: 'TODAS', rotulo: 'Todas' },
] as const;

/**
 * Renovações e a revisão preventiva do 9º mês.
 *
 * A revisão existe para que a renovação não seja surpresa: três meses antes,
 * alguém olha o contrato e decide se há proposta melhor a fazer. Por isso o
 * que a tela cobra não é a renovação vencida — é a revisão não feita.
 */
export function PaginaRenovacoes(): React.JSX.Element {
  const [filtro, definirFiltro] = useState<(typeof FILTROS)[number]['valor']>('ABERTAS');
  const [editando, definirEditando] = useState<Renovacao | null>(null);

  const consulta = useQuery({
    queryKey: ['renovacoes', filtro],
    queryFn: () => chamarApi<Renovacao[]>(`/renovacoes?filtro=${filtro}`),
  });

  // Os indicadores falam de toda a carteira, não do filtro escolhido.
  const consultaDeTodas = useQuery({
    queryKey: ['renovacoes', 'TODAS'],
    queryFn: () => chamarApi<Renovacao[]>('/renovacoes?filtro=TODAS'),
  });

  const renovacoes = consulta.data ?? [];
  const todas = consultaDeTodas.data ?? [];
  const hoje = new Date();
  const em90Dias = new Date(hoje.getTime() + 90 * 24 * 60 * 60 * 1000);
  const revisaoVencida = renovacoes.filter(
    (linha) => linha.revisaoAtiva && linha.dataRevisao && new Date(linha.dataRevisao) <= hoje,
  );
  const emAberto = todas.filter((linha) => !['RENOVADA', 'PERDIDA'].includes(linha.status));

  return (
    <section>
      <CabecalhoDePagina
        area="Retenção"
        titulo="Renovações"
        descricao="Agenda de renovação dos contratos, reajustes, propostas e retenção."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Indicador
          rotulo="Revisão 9º mês"
          valor={String(
            emAberto.filter(
              (linha) => linha.revisaoAtiva && linha.dataRevisao && new Date(linha.dataRevisao) <= hoje,
            ).length,
          )}
          severidade="alerta"
        />
        <Indicador
          rotulo="Próximos 90 dias"
          valor={String(
            emAberto.filter(
              (linha) =>
                linha.dataRenovacao &&
                new Date(linha.dataRenovacao) >= hoje &&
                new Date(linha.dataRenovacao) <= em90Dias,
            ).length,
          )}
        />
        <Indicador
          rotulo="Em negociação"
          valor={String(
            todas.filter((linha) =>
              ['CONTATO_INICIADO', 'EM_NEGOCIACAO', 'PROPOSTA_ENVIADA'].includes(linha.status),
            ).length,
          )}
        />
        <Indicador
          rotulo="Renovadas"
          valor={String(todas.filter((linha) => linha.status === 'RENOVADA').length)}
          severidade="sucesso"
        />
        <Indicador
          rotulo="Valor em renovação"
          valor={formatarReais(
            emAberto.reduce((total, linha) => total + linha.valorAtualCentavos, 0),
          )}
        />
      </div>

      <Cartao
        className="mt-3"
        titulo="Avisos de revisão no 9º mês"
        descricao="Ao completar 9 meses de vigência, o contrato entra nesta fila para análise."
      >
        {revisaoVencida.length === 0 ? (
          <p className="py-4.5 text-xs text-texto-suave">
            Nenhum contrato entrou na janela de revisão do 9º mês.
          </p>
        ) : (
          revisaoVencida.slice(0, 5).map((linha) => (
            <LinhaDeLista
              key={linha.id}
              titulo={linha.contato.nome}
              detalhe={`${linha.produto}${linha.operadora ? ` · ${linha.operadora}` : ''} · revisão ${data(linha.dataRevisao)} · renovação ${data(linha.dataRenovacao)}`}
              valor={<Pilula tom="dourado">Revisar contrato</Pilula>}
            />
          ))
        )}
      </Cartao>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTROS.map((opcao) => (
          <Botao
            key={opcao.valor}
            variante={filtro === opcao.valor ? 'primario' : 'secundario'}
            onClick={() => definirFiltro(opcao.valor)}
          >
            {opcao.rotulo}
          </Botao>
        ))}
      </div>

      <Cartao
        className="mt-3"
        titulo="Agenda de renovações"
        descricao="A renovação é sugerida a partir da vigência do contrato."
      >
        <Tabela
          colunas={[
            'Revisão 9º mês',
            'Renovação',
            'Cliente',
            'Produto',
            'Operadora',
            'Responsável',
            'Valor atual',
            'Proposta',
            'Status',
            'Ação',
          ]}
          vazio="Nenhuma renovação neste filtro."
          carregando={consulta.isLoading}
        >
          {renovacoes.map((renovacao) => (
            <Linha key={renovacao.id}>
              <Celula>
                {renovacao.revisaoAtiva ? (
                  data(renovacao.dataRevisao)
                ) : (
                  <span className="text-texto-suave">Desativada</span>
                )}
              </Celula>
              <Celula>{data(renovacao.dataRenovacao)}</Celula>
              <Celula className="font-medium">{renovacao.contato.nome}</Celula>
              <Celula>{renovacao.produto}</Celula>
              <Celula>{renovacao.operadora ?? '—'}</Celula>
              <Celula>{renovacao.responsavel.nome}</Celula>
              <Celula>{formatarValorMensal(renovacao.valorAtualCentavos)}</Celula>
              <Celula>
                {renovacao.valorPropostoCentavos === null
                  ? '—'
                  : formatarReais(renovacao.valorPropostoCentavos)}
              </Celula>
              <Celula>
                <Pilula tom={TOM_DO_STATUS_DA_RENOVACAO[renovacao.status]}>
                  {ROTULO_STATUS_RENOVACAO[renovacao.status]}
                </Pilula>
              </Celula>
              <Celula>
                <Botao tamanho="mini" onClick={() => definirEditando(renovacao)}>
                  Editar
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Tabela>
      </Cartao>

      {editando ? (
        <JanelaDaRenovacao renovacao={editando} aoFechar={() => definirEditando(null)} />
      ) : null}
    </section>
  );
}

/**
 * A janela "Editar renovação" do protótipo.
 *
 * O `PATCH /renovacoes/:id` aceita oito campos; a tela mandava dois, cada um
 * por um `<select>` solto na linha. Conforme a conversa com o cliente avança —
 * proposta enviada, valor negociado, renovação aceita — o que muda são vários
 * campos de uma vez, e um por vez a lista piscava a cada clique.
 *
 * Criar renovação à mão continua de fora: ela nasce da vigência do Contrato,
 * por `abrirRenovacao`, e não existe rota para criá-la avulsa. Construir uma
 * abriria caminho para renovação sem contrato.
 */
function JanelaDaRenovacao({
  renovacao,
  aoFechar,
}: {
  renovacao: Renovacao;
  aoFechar: () => void;
}): React.JSX.Element {
  const { usuaria } = useSessao();
  const clienteDeConsultas = useQueryClient();
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';

  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Array<{ id: string; nome: string; ativa: boolean }>>('/usuarias'),
  });
  const responsaveis = (usuarias.data ?? []).filter((uma) => uma.ativa);

  /**
   * Controlado: a lista de usuárias chega por consulta, depois da primeira
   * pintura. Num `<select>` não-controlado o navegador assume a primeira opção
   * quando ela aparece, trocando a responsável sem ninguém pedir.
   *
   * O estado guarda só a escolha de quem preencheu; enquanto ninguém mexeu,
   * ele é nulo e o campo mostra o que está gravado.
   */
  const [responsavelEscolhido, escolherResponsavel] = useState<string | null>(null);
  const responsavelId = responsavelEscolhido ?? renovacao.responsavel.id;

  const salvar = useMutation({
    mutationFn: (campos: FormData) =>
      chamarApi(`/renovacoes/${renovacao.id}`, {
        metodo: 'PATCH',
        corpo: {
          status: String(campos.get('status')) as StatusRenovacao,
          statusRevisao: String(campos.get('statusRevisao')) as StatusRevisao,
          revisaoAtiva: campos.get('revisaoAtiva') === 'on',
          ...dataInformada(campos, 'dataRevisao'),
          ...dataInformada(campos, 'dataRenovacao'),
          valorPropostoCentavos: emCentavos(campos.get('valorProposto')),
          observacoes: String(campos.get('observacoes') ?? '').trim() || null,
          ...(ehAdministradora ? { responsavelId } : {}),
        },
      }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['renovacoes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['revisoes-pendentes'] });
      aoFechar();
    },
  });

  return (
    <Dialogo
      area="Retenção"
      titulo="Editar renovação"
      descricao={`${renovacao.contato.nome} · ${renovacao.produto} · ${formatarValorMensal(renovacao.valorAtualCentavos)} / mês`}
      aoFechar={aoFechar}
    >
      <form
        data-teste="formulario-renovacao"
        onSubmit={(evento) => {
          evento.preventDefault();
          salvar.mutate(new FormData(evento.currentTarget));
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <CampoSelecao rotulo="Status" name="status" defaultValue={renovacao.status}>
            {STATUS_RENOVACAO.map((status) => (
              <option key={status} value={status}>
                {ROTULO_STATUS_RENOVACAO[status]}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao
            rotulo="Status da revisão"
            name="statusRevisao"
            defaultValue={renovacao.statusRevisao}
          >
            {STATUS_REVISAO.map((status) => (
              <option key={status} value={status}>
                {ROTULO_STATUS_REVISAO[status]}
              </option>
            ))}
          </CampoSelecao>

          {/* Obrigatórias só quando já gravadas: a API troca as datas e não
            * aceita apagá-las, mas as colunas são anuláveis. Exigir sempre
            * trancaria a janela de uma renovação sem data — e é a data de
            * revisão que põe o contrato na fila do 9º mês. */}
          <CampoTexto
            rotulo="Data da revisão"
            name="dataRevisao"
            type="date"
            required={Boolean(renovacao.dataRevisao)}
            defaultValue={dia(renovacao.dataRevisao)}
          />
          <CampoTexto
            rotulo="Data da renovação"
            name="dataRenovacao"
            type="date"
            required={Boolean(renovacao.dataRenovacao)}
            defaultValue={dia(renovacao.dataRenovacao)}
          />

          <CampoTexto
            rotulo="Valor proposto (R$)"
            name="valorProposto"
            type="number"
            step="0.01"
            min="0"
            dica="Em branco enquanto não houver proposta na mesa."
            defaultValue={
              renovacao.valorPropostoCentavos === null
                ? ''
                : (renovacao.valorPropostoCentavos / 100).toFixed(2)
            }
          />

          <CampoSelecao
            rotulo="Responsável"
            value={responsavelId}
            disabled={!ehAdministradora}
            onChange={(evento) => escolherResponsavel(evento.target.value)}
          >
            {responsaveis.map((uma) => (
              <option key={uma.id} value={uma.id}>
                {uma.nome}
              </option>
            ))}
          </CampoSelecao>
        </div>

        <CampoCaixa
          rotulo="Revisão do 9º mês ativa"
          name="revisaoAtiva"
          className="mt-3"
          defaultChecked={renovacao.revisaoAtiva}
        />

        <CampoTextoLongo
          rotulo="Observações"
          name="observacoes"
          maxLength={2000}
          defaultValue={renovacao.observacoes ?? ''}
          className="mt-3"
        />

        <RecusaDaApi erro={salvar.error} padrao="Não foi possível salvar a renovação." />

        <div className="mt-4 flex justify-end gap-2">
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao tipo="submit" variante="primario" desabilitado={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar renovação'}
          </Botao>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * A data só entra no corpo quando tem valor.
 *
 * O contrato troca as datas e não aceita apagá-las — `z.iso.date()` sem
 * `nullish`. Mandar string vazia faria a API recusar a gravação inteira de
 * uma renovação que ainda não tem a data.
 */
function dataInformada(campos: FormData, nome: string): Record<string, string> {
  const valor = String(campos.get(nome) ?? '').trim();
  return valor ? { [nome]: valor } : {};
}

/**
 * O valor proposto vai em centavos, como o contrato da API espera.
 *
 * Em branco é nulo de propósito: significa que ainda não há proposta na mesa,
 * e não uma proposta de zero real.
 */
function emCentavos(valor: FormDataEntryValue | null): number | null {
  const bruto = String(valor ?? '').trim();
  return bruto === '' ? null : paraCentavos(bruto);
}
